<template>
  <div :style="omit(style, ['paddingBottom', 'paddingLeft', 'paddingTop', 'paddingRight'])">
    <PlaceholderComponent v-if="shouldDisplayPlaceholderComponent(isEditMode, mode, visible)" :name="tracking_id" />
    <EditableComponent
      v-else-if="isEditMode && !isViewOnlyMode && componentPermissions.hasSomethingEditable"
      :id="id"
      :active="componentEditMode && componentEditMode?.id === id"
      :collapsible="false"
      :duplicate="componentPermissions.canDuplicate"
      :edit="componentPermissions.canEdit"
      :parent-type="parentType"
      :remove="componentPermissions.canRemove"
      :resizable="false"
      :selected="componentSelectedMode && componentSelectedMode?.id === id"
      :settings="!usedInSectionId"
      :stylable="!usedInSectionId"
      @duplicate="duplicate(id)"
      @edit="setComponentEditMode(ComponentTypes.CustomDataTable, id)"
      @remove="removeComponentById(id)"
      @select="setComponentSelectedMode(id)"
      @settings="setComponentSettingsMode(id)"
      @style="emit('style', $event)"
    >
      <RawCustomDataTable
        v-if="!isEmpty(data)"
        :id="id"
        :key="JSON.stringify(data)"
        :data="dataWithOverrides"
        :style="style"
        @sortChanged="handleSortChanged"
      />
      <EditCustomDataTable v-else :style="placeholderStyle" />
    </EditableComponent>
    <RawCustomDataTable v-else :id="id" :data="dataWithOverrides" :style="style" @sortChanged="handleSortChanged" />
  </div>
</template>

<script setup lang="ts">
import { type ColumnSort } from '@tanstack/table-core'
import { isEmpty, omit } from 'lodash-es'
import { computed, toRefs, onMounted } from 'vue'

import EditableComponent from '@canvas-builder/components/container/EditableComponent/EditableComponent.vue'
import EditCustomDataTable from './CustomDataTable.edit.vue'
import RawCustomDataTable from './CustomDataTable.raw.vue'

import {
  ComponentTypes,
  type ComponentNode,
  type CustomDataTableProps,
  type DynamicDatatableProperty,
} from '@canvas-builder/types/canvas'
import { defaultWhenUsedInSection } from '@canvas-builder/constants'
import { type CanvasRetrieve } from '@openapi'
import useComponentPermissions from '@canvas-builder/composables/useComponentPermissions'
import { shouldDisplayPlaceholderComponent } from '@canvas-builder/util/canvas.util'
import PlaceholderComponent from '@canvas-builder/components/container/PlaceholderComponent/PlaceholderComponent.vue'
import useCanvas from '@canvas-builder/composables/useCanvas'
import { type CanvasMetadataRetrieve } from '@lib/types/canvases'
import { useCoreApi } from '@lib/composables/useCoreApi'

interface CustomDataTableUserSorting {
  field: string
  direction: 'asc' | 'desc'
}

interface CustomDataTableCanvasMetadata {
  user_sorting?: CustomDataTableUserSorting
}

const props = withDefaults(
  defineProps<{
    id: string
    data: CustomDataTableProps
    children?: ComponentNode[]
    parentType?: ComponentTypes
    usedInSectionId?: CanvasRetrieve['id']
    when_used_in_section?: ComponentNode['when_used_in_section']
    visible?: ComponentNode['visible']
    tracking_id?: string
    style?: Record<string, any>
    theme_meta?: ComponentNode['theme_meta']
  }>(),
  {
    children: () => [],
    when_used_in_section: () => defaultWhenUsedInSection,
    usedInSectionId: '',
  },
)

const emit = defineEmits<{
  style: [s: Record<string, any>, id?: ComponentNode['id']]
}>()

const { id, usedInSectionId, when_used_in_section } = toRefs(props)

const {
  activeCanvas,
  canvasMetadata,
  componentEditMode,
  componentSelectedMode,
  duplicate,
  isImpact,
  isEditMode,
  isViewOnlyMode,
  justAddedComponentId,
  mode,
  removeComponentById,
  setComponentEditMode,
  setCanvasMetadata,
  setComponentSelectedMode,
  setComponentSettingsMode,
} = useCanvas()
const { coreRestApiAxios } = useCoreApi()

const componentPermissions = useComponentPermissions({
  id,
  usedInSectionId,
  when_used_in_section,
})

const placeholderStyle = computed(() => {
  return {
    ...props.style,
  }
})

const dataWithOverrides = computed(() => {
  const componentCanvasMetadata: CustomDataTableCanvasMetadata =
    canvasMetadata.value?.presentation_component_metadata?.[props.id]

  const userSorting = componentCanvasMetadata?.user_sorting
  if (!userSorting) return props.data

  // Check if the field exists in data.columns and is sortable
  const sortableField = props.data.columns?.find((prop) => prop.key === userSorting.field && prop.sort.enabled)
  if (!sortableField) return props.data

  // Return data with updated sorting for the specified field
  return {
    ...props.data,
    columns: props.data.columns?.map((prop: DynamicDatatableProperty) =>
      prop.key === userSorting.field
        ? { ...prop, sort: { ...prop.sort, default: true, direction: userSorting.direction } }
        : { ...prop, sort: { ...prop.sort, default: false } },
    ),
  }
})

async function handleSortChanged(sorting: ColumnSort | null) {
  const field = sorting?.id ?? null
  const direction = sorting?.desc ? 'desc' : 'asc'

  if (!isImpact.value || !coreRestApiAxios?.value) {
    return
  }

  try {
    const updatedCanvasMetadata = await coreRestApiAxios.value.patch<unknown, CanvasMetadataRetrieve['config']>(
      `protected/canvases/${activeCanvas.value?.id}/metadata`,
      {
        config: {
          presentation_component_metadata: {
            [props.id]: field ? { user_sorting: { field, direction } } : undefined,
          },
        },
      },
    )

    setCanvasMetadata(updatedCanvasMetadata.config)
  } catch (error) {
    console.error('[CustomDataTable.vue]: failed to update canvas metadata', error)
  }
}

onMounted(() => {
  if (justAddedComponentId.value === props.id) {
    justAddedComponentId.value = null
    setComponentEditMode(ComponentTypes.CustomDataTable, props.id)
  }
})
</script>
